import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Bookmark, StickyNote, Clock, Flame, Trophy, 
  ChevronRight, Trash2, Edit2, Plus, X, Check, Award, CheckCircle2, ShieldCheck,
  Target, Calendar, Eye, EyeOff, Users, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import BadgeDisplay from '../components/BadgeDisplay';
import ReadingHeatmap from '../components/ReadingHeatmap';
import UserAvatar from '../components/avatars/UserAvatar';
import BookwormCard from '../components/avatars/BookwormCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAssetBaseUrl = () => window.location.origin;

const getCoverUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${getAssetBaseUrl()}${url}`;
  return url;
};

const MyLibrary = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  
  const [activeTab, setActiveTab] = useState('history');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  
  // Note editing
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  // Verified reading progress from AI accuracy system
  const [verifiedProgress, setVerifiedProgress] = useState({});

  // Goals state
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState({ goal_type: 'daily', metric: 'pages', target_value: 30 });

  // Community state
  const [communityReading, setCommunityReading] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLibraryData();
    fetchGoals();
    fetchCommunity();
  }, []);

  // Fetch verified progress for all books in history
  useEffect(() => {
    if (history.length > 0) {
      fetchVerifiedProgress();
    }
  }, [history]);

  const fetchVerifiedProgress = async () => {
    const progressMap = {};
    await Promise.all(
      history.map(async (item) => {
        try {
          const res = await axios.get(`${API}/user-progress/${item.book_id}`, { headers });
          progressMap[item.book_id] = res.data;
        } catch {
          progressMap[item.book_id] = null;
        }
      })
    );
    setVerifiedProgress(progressMap);
  };

  const fetchLibraryData = async () => {
    setLoading(true);
    try {
      const [statsRes, historyRes, bookmarksRes, notesRes] = await Promise.all([
        axios.get(`${API}/library/stats`, { headers }),
        axios.get(`${API}/library/history`, { headers }),
        axios.get(`${API}/library/bookmarks`, { headers }),
        axios.get(`${API}/library/notes`, { headers })
      ]);
      
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setBookmarks(bookmarksRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      console.error('Error fetching library data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load library data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteBookmark = async (bookmarkId) => {
    try {
      await axios.delete(`${API}/library/bookmarks/${bookmarkId}`, { headers });
      setBookmarks(bookmarks.filter(b => b.id !== bookmarkId));
      toast({ title: 'Bookmark deleted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete bookmark', variant: 'destructive' });
    }
  };

  const deleteHistory = async (bookId, e) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    if (!window.confirm('Remove this book from your reading history?')) return;
    
    try {
      await axios.delete(`${API}/library/history/${bookId}`, { headers });
      setHistory(history.filter(h => h.book_id !== bookId));
      toast({ title: 'Removed from history' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove from history', variant: 'destructive' });
    }
  };

  const markAsComplete = async (bookId, e) => {
    e.stopPropagation();
    
    // Check verified reading progress before allowing completion
    const vp = verifiedProgress[bookId];
    const verifiedPct = vp?.verified_percentage || 0;
    if (verifiedPct < 95) {
      toast({
        title: 'Cannot mark as complete',
        description: `You've verifiably read ${verifiedPct.toFixed(1)}% of this book. You need at least 95% verified reading to complete it. Keep reading!`,
        variant: 'destructive'
      });
      return;
    }
    
    try {
      await axios.post(
        `${API}/books/${bookId}/progress`,
        { progress_percentage: 100 },
        { headers }
      );
      
      // Update local state
      setHistory(history.map(h => 
        h.book_id === bookId 
          ? { ...h, progress_percentage: 100, status: 'completed' }
          : h
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        completed_books: (prev.completed_books || 0) + 1
      }));
      
      toast({ title: '🎉 Book marked as complete!' });
      
      // Check for new badges
      try {
        await axios.post(`${API}/user/check-badges`, {}, { headers });
      } catch (err) {
        console.log('Badge check skipped');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark as complete', variant: 'destructive' });
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/library/notes/${noteId}`, { headers });
      setNotes(notes.filter(n => n.id !== noteId));
      toast({ title: 'Note deleted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete note', variant: 'destructive' });
    }
  };

  const updateNote = async (noteId) => {
    try {
      await axios.put(`${API}/library/notes/${noteId}`, { content: editContent }, { headers });
      setNotes(notes.map(n => n.id === noteId ? { ...n, content: editContent } : n));
      setEditingNote(null);
      toast({ title: 'Note updated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update note', variant: 'destructive' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'partial': return 'text-yellow-400';
      default: return 'text-blue-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'partial': return 'In Progress';
      default: return 'Started';
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/user/goals`, { headers });
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoadingGoals(false);
    }
  };

  const fetchCommunity = async () => {
    try {
      const response = await axios.get(`${API}/user/community/reading-now`);
      setCommunityReading(response.data);
    } catch (error) {
      console.error('Error fetching community:', error);
    }
  };

  const handleCreateGoal = async () => {
    try {
      const response = await axios.post(`${API}/user/goals`, newGoal, { headers });
      setGoals([...goals, response.data]);
      setShowGoalModal(false);
      toast({ title: 'Goal Created', description: `Your ${newGoal.goal_type} reading goal has been set!` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create goal', variant: 'destructive' });
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/user/goals/${goalId}`, { headers });
      setGoals(goals.filter(g => g.id !== goalId));
      toast({ title: 'Goal removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete goal', variant: 'destructive' });
    }
  };

  const handleToggleGoal = async (goal) => {
    try {
      await axios.put(`${API}/user/goals/${goal.id}`, { is_active: !goal.is_active }, { headers });
      setGoals(goals.map(g => g.id === goal.id ? { ...g, is_active: !g.is_active } : g));
      toast({ title: goal.is_active ? 'Goal Paused' : 'Goal Activated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update goal', variant: 'destructive' });
    }
  };

  const handleAddProgress = async (goalId, increment) => {
    try {
      await axios.post(`${API}/user/goals/${goalId}/progress`, { increment }, { headers });
      fetchGoals();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update progress', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-white text-xl">Loading your library...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
            <BookOpen className="h-10 w-10 mr-3 text-red-500" />
            My Library
          </h1>
          <p className="text-gray-400">Your reading history, bookmarks, and notes</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <BookOpen className="h-6 w-6 text-blue-400" />
                <span className="text-2xl font-bold text-white">{stats.total_books_read}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">Books Read</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <Check className="h-6 w-6 text-green-400" />
                <span className="text-2xl font-bold text-white">{stats.books_completed}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">Completed</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <Clock className="h-6 w-6 text-yellow-400" />
                <span className="text-2xl font-bold text-white">{stats.books_in_progress}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">In Progress</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <Bookmark className="h-6 w-6 text-purple-400" />
                <span className="text-2xl font-bold text-white">{stats.bookmarks_count}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">Bookmarks</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <StickyNote className="h-6 w-6 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.notes_count}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">Notes</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <Flame className="h-6 w-6 text-red-400" />
                <span className="text-2xl font-bold text-white">{stats.current_streak}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">Day Streak</p>
            </div>
          </div>
        )}

        {/* GitHub-style reading heatmap — 365 days of pages read. */}
        <ReadingHeatmap days={365} />

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-white/10 pb-4">
          <Button
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-history"
          >
            <Clock className="h-4 w-4 mr-2" />
            Reading History ({history.length})
          </Button>
          <Button
            variant={activeTab === 'bookmarks' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('bookmarks')}
            className={activeTab === 'bookmarks' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-bookmarks"
          >
            <Bookmark className="h-4 w-4 mr-2" />
            Bookmarks ({bookmarks.length})
          </Button>
          <Button
            variant={activeTab === 'notes' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('notes')}
            className={activeTab === 'notes' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-notes"
          >
            <StickyNote className="h-4 w-4 mr-2" />
            Notes ({notes.length})
          </Button>
          <Button
            variant={activeTab === 'badges' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('badges')}
            className={activeTab === 'badges' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-badges"
          >
            <Award className="h-4 w-4 mr-2" />
            Badges
          </Button>
          <Button
            variant={activeTab === 'goals' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('goals')}
            className={activeTab === 'goals' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-goals"
          >
            <Target className="h-4 w-4 mr-2" />
            Goals ({goals.filter(g => g.is_active).length})
          </Button>
          <Button
            variant={activeTab === 'community' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('community')}
            className={activeTab === 'community' ? 'bg-red-600' : 'text-gray-400 hover:text-white'}
            data-testid="tab-community"
          >
            <Users className="h-4 w-4 mr-2" />
            Community
          </Button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Reading History Tab */}
          {activeTab === 'history' && (
            <>
              {history.length === 0 ? (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
                  <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No reading history yet</h3>
                  <p className="text-gray-400 mb-4">Start reading to track your progress</p>
                  <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
                    Browse Books
                  </Button>
                </div>
              ) : (
                history.map((item) => (
                  <div
                    key={`${item.book_id}-${item.started_at}`}
                    className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center space-x-4 hover:bg-white/10 transition-colors group"
                    data-testid="history-item"
                  >
                    <img
                      src={getCoverThumbUrl(item.book_cover)}
                      alt={item.book_title}
                      className="w-16 h-24 object-cover rounded-lg cursor-pointer"
                      onClick={() => navigate(`/book/${item.book_id}`)}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                      }}
                    />
                    <div className="flex-1 cursor-pointer" onClick={() => navigate(`/book/${item.book_id}`)}>
                      <h3 className="text-lg font-semibold text-white">{item.book_title}</h3>
                      <p className="text-gray-400 text-sm">{item.book_author}</p>
                      <div className="flex items-center mt-2 space-x-4">
                        <span className={`text-sm font-medium ${getStatusColor(item.status)}`}>
                          {getStatusLabel(item.status)}
                        </span>
                        <div className="flex-1 max-w-xs bg-white/10 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${Math.min(item.progress_percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-gray-400 text-sm">{Math.round(item.progress_percentage)}%</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Mark Complete Button - disabled unless 95% verified reading */}
                      {item.status !== 'completed' && (() => {
                        const vp = verifiedProgress[item.book_id];
                        const verifiedPct = vp?.verified_percentage || 0;
                        const canComplete = verifiedPct >= 95;
                        return (
                          <div className="flex items-center gap-2">
                            {vp && (
                              <span className="text-[10px] text-zinc-500 flex items-center gap-1" data-testid="verified-reading-label">
                                <ShieldCheck className="w-3 h-3 text-blue-400" />
                                {verifiedPct.toFixed(0)}% verified
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => markAsComplete(item.book_id, e)}
                              className={canComplete
                                ? "text-green-400 hover:text-green-300 hover:bg-green-500/10 cursor-pointer"
                                : "text-zinc-600 cursor-not-allowed opacity-50"
                              }
                              disabled={!canComplete}
                              title={canComplete ? "Mark as complete" : `Read ${(95 - verifiedPct).toFixed(0)}% more to unlock`}
                              data-testid="mark-complete-btn"
                            >
                              <CheckCircle2 className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline text-xs">Complete</span>
                            </Button>
                          </div>
                        );
                      })()}
                      {item.status === 'completed' && (
                        <div className="flex items-center text-green-400 text-xs px-2">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Done</span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/book/${item.book_id}`)}
                        className="text-gray-400 hover:text-white hover:bg-white/10"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => deleteHistory(item.book_id, e)}
                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="delete-history-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* Bookmarks Tab */}
          {activeTab === 'bookmarks' && (
            <>
              {bookmarks.length === 0 ? (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
                  <Bookmark className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No bookmarks yet</h3>
                  <p className="text-gray-400">Bookmark pages while reading to find them later</p>
                </div>
              ) : (
                bookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center space-x-4"
                    data-testid="bookmark-item"
                  >
                    <img
                      src={getCoverThumbUrl(bookmark.book_cover)}
                      alt={bookmark.book_title}
                      className="w-12 h-18 object-cover rounded-lg cursor-pointer"
                      onClick={() => navigate(`/book/${bookmark.book_id}`)}
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">{bookmark.book_title}</h3>
                      <p className="text-gray-400 text-sm">{bookmark.book_author}</p>
                      <div className="flex items-center mt-1 text-sm">
                        <Bookmark className="h-4 w-4 text-purple-400 mr-1" />
                        <span className="text-purple-400">{bookmark.label}</span>
                        {bookmark.page_number && (
                          <span className="text-gray-500 ml-2">Page {bookmark.page_number}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteBookmark(bookmark.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <>
              {notes.length === 0 ? (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
                  <StickyNote className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No notes yet</h3>
                  <p className="text-gray-400">Add notes while reading to remember key insights</p>
                </div>
              ) : (
                notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-white/5 rounded-xl p-4 border border-white/10"
                    data-testid="note-item"
                  >
                    <div className="flex items-start space-x-4">
                      <img
                        src={getCoverThumbUrl(note.book_cover)}
                        alt={note.book_title}
                        className="w-12 h-18 object-cover rounded-lg cursor-pointer"
                        onClick={() => navigate(`/book/${note.book_id}`)}
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">{note.book_title}</h3>
                        <p className="text-gray-400 text-sm mb-2">{note.book_author}</p>
                        
                        {note.highlight_text && (
                          <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-2 mb-2 rounded">
                            <p className="text-yellow-200 text-sm italic">"{note.highlight_text}"</p>
                          </div>
                        )}
                        
                        {editingNote === note.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="bg-white/10 border-white/20 text-white"
                            />
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                onClick={() => updateNote(note.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Check className="h-4 w-4 mr-1" /> Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingNote(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-200">{note.content}</p>
                        )}
                        
                        <p className="text-gray-500 text-xs mt-2">
                          {new Date(note.created_at).toLocaleDateString()}
                          {note.page_number && ` • Page ${note.page_number}`}
                        </p>
                      </div>
                      
                      {editingNote !== note.id && (
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingNote(note.id);
                              setEditContent(note.content);
                            }}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNote(note.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <BadgeDisplay />
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && (
            <div data-testid="goals-tab-content">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">Set daily or weekly targets to stay motivated</p>
                <Button
                  size="sm"
                  onClick={() => setShowGoalModal(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="add-goal-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> New Goal
                </Button>
              </div>

              {loadingGoals ? (
                <div className="text-gray-400 text-center py-8">Loading goals...</div>
              ) : goals.length === 0 ? (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
                  <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No reading goals yet</h3>
                  <p className="text-gray-400 mb-4">Set a daily or weekly goal to track your progress</p>
                  <Button onClick={() => setShowGoalModal(true)} className="bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4 mr-1" /> Create Your First Goal
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {goals.map((goal) => {
                    const progress = goal.completion_percentage ?? Math.min(100, Math.round(((goal.current_progress ?? 0) / (goal.target_value || 1)) * 100));
                    return (
                      <div
                        key={goal.id}
                        className={`p-5 rounded-xl border ${
                          goal.is_completed
                            ? 'bg-green-500/10 border-green-500/30'
                            : goal.is_active
                              ? 'bg-white/5 border-white/10'
                              : 'bg-white/5 border-white/5 opacity-60'
                        }`}
                        data-testid={`goal-${goal.id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            {goal.goal_type === 'daily' ? (
                              <Calendar className="h-5 w-5 text-blue-400" />
                            ) : (
                              <Clock className="h-5 w-5 text-purple-400" />
                            )}
                            <span className="text-white font-medium capitalize">{goal.goal_type} Goal</span>
                            <span className="text-gray-500 text-sm capitalize">({goal.metric || 'pages'})</span>
                            {goal.is_completed && <CheckCircle className="h-4 w-4 text-green-400" />}
                          </div>
                          <div className="flex items-center space-x-1">
                            {goal.is_active && !goal.is_completed && (
                              <Button variant="ghost" size="sm" onClick={() => handleAddProgress(goal.id, 1)}
                                className="text-green-400 hover:bg-green-500/20 h-8 w-8 p-0" data-testid={`goal-increment-${goal.id}`}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleToggleGoal(goal)}
                              className="text-gray-400 hover:text-white h-8 w-8 p-0">
                              {goal.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteGoal(goal.id)}
                              className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-400">{goal.current_progress ?? 0} / {goal.target_value} {goal.metric || 'pages'}</span>
                          <span className="text-white font-medium">{progress}%</span>
                        </div>
                        <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              goal.is_completed ? 'bg-green-500' : 'bg-gradient-to-r from-red-500 to-orange-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Community Tab */}
          {activeTab === 'community' && (
            <div data-testid="community-tab-content">
              <p className="text-gray-400 text-sm mb-4">See what other readers are currently enjoying</p>
              {communityReading.length === 0 ? (
                <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
                  <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No one reading right now</h3>
                  <p className="text-gray-400">Start reading a book to appear here!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {communityReading.map((reader, idx) => (
                    <div
                      key={`${reader.user_id || reader.user_name || 'anon'}-${reader.book_id || idx}`}
                      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors cursor-pointer"
                      onClick={() => navigate(`/book/${reader.book_id}`)}
                      data-testid={`community-reader-${idx}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <BookwormCard
                          userId={reader.user_id}
                          name={reader.user_name}
                          avatarId={reader.user_avatar_id}
                          avatarPhotoUrl={reader.user_avatar_photo_url}
                        >
                          <UserAvatar
                            name={reader.user_name}
                            avatarId={reader.user_avatar_id}
                            avatarPhotoUrl={reader.user_avatar_photo_url}
                            size="md"
                            testId={`community-reader-avatar-${idx}`}
                          />
                        </BookwormCard>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{reader.user_name}</p>
                          <p className="text-gray-500 text-xs">is reading</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        {reader.cover_image_url && (
                          <img
                            src={getCoverThumbUrl(reader.cover_image_url)}
                            alt={reader.book_title}
                            className="w-12 h-18 object-cover rounded-lg flex-shrink-0"
                            loading="lazy"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium line-clamp-2">{reader.book_title}</p>
                          <p className="text-gray-400 text-xs mt-1">{reader.book_author}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Goal Creation Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <Target className="h-6 w-6 text-red-500 mr-2" />
              Set Reading Goal
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2 block">Goal Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setNewGoal({ ...newGoal, goal_type: 'daily' })}
                    className={`p-3 rounded-lg border transition-all ${newGoal.goal_type === 'daily' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                    data-testid="goal-type-daily">
                    <Calendar className="h-5 w-5 mx-auto mb-1" /> Daily
                  </button>
                  <button type="button" onClick={() => setNewGoal({ ...newGoal, goal_type: 'weekly' })}
                    className={`p-3 rounded-lg border transition-all ${newGoal.goal_type === 'weekly' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                    data-testid="goal-type-weekly">
                    <Clock className="h-5 w-5 mx-auto mb-1" /> Weekly
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Track By</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setNewGoal({ ...newGoal, metric: 'pages' })}
                    className={`p-3 rounded-lg border transition-all ${newGoal.metric === 'pages' ? 'bg-green-600 border-green-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                    data-testid="goal-metric-pages">
                    <BookOpen className="h-5 w-5 mx-auto mb-1" /> Pages
                  </button>
                  <button type="button" onClick={() => setNewGoal({ ...newGoal, metric: 'minutes' })}
                    className={`p-3 rounded-lg border transition-all ${newGoal.metric === 'minutes' ? 'bg-orange-600 border-orange-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'}`}
                    data-testid="goal-metric-minutes">
                    <Clock className="h-5 w-5 mx-auto mb-1" /> Minutes
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Target ({newGoal.metric})</Label>
                <Input type="number" min="1" max="1000" value={newGoal.target_value}
                  onChange={(e) => setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) || 1 })}
                  className="bg-white/10 border-white/20 text-white" data-testid="goal-target-input" />
                <p className="text-gray-500 text-xs mt-1">
                  {newGoal.goal_type === 'daily' ? `Read ${newGoal.target_value} ${newGoal.metric} every day` : `Read ${newGoal.target_value} ${newGoal.metric} per week`}
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowGoalModal(false)} className="flex-1 border-white/20 text-white hover:bg-white/10">Cancel</Button>
              <Button onClick={handleCreateGoal} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="create-goal-btn">Create Goal</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLibrary;
